<script setup lang="ts">
import { ref, watch } from "vue";
import { usePersonProfileAction } from "@/composables/people/usePersonProfileAction";
import { useCardService } from "../../../cbi-generator/frontend/composables/useCardFrontendService";
import type { CardStaffSearchResult } from "../../../cbi-generator/generated/dto/CardStaffSearchResult";

const props = defineProps<{ personId: number; card?: CardStaffSearchResult }>();
const emit = defineEmits<{ saved: [] }>();
const service = useCardService();
const { saving, error, fieldErrors, clear, save } = usePersonProfileAction();
const dialog = ref(false);
const cardNum = ref("");
const active = ref(true);
const issueDate = ref<Date | null>(new Date());
watch(dialog, (open) => {
  if (open) {
    clear();
    cardNum.value = props.card?.cardNum ?? "";
    active.value = props.card?.active === true || !props.card;
    issueDate.value = new Date();
  }
});
async function submit() {
  if (!/^\d{1,7}$/.test(cardNum.value)) return;
  const issuedOn = issueDate.value;
  if (!issuedOn) return;
  const person = { personId: props.personId };
  if (
    await save(() =>
      props.card
        ? service.editCardStaff({
            person,
            card: { assignId: props.card.assignId },
            cardNum: cardNum.value,
            active: active.value,
          })
        : service.issueFreeCardStaff({
            person,
            cardNum: cardNum.value,
            issueDate: issuedOn,
            active: active.value,
          }),
    )
  ) {
    dialog.value = false;
    emit("saved");
  }
}
</script>

<template>
  <v-dialog v-model="dialog" max-width="550" :persistent="saving">
    <template #activator="{ props: activator }">
      <v-btn
        v-bind="activator"
        :aria-label="card ? 'Edit card' : 'Add free card'"
        :icon="card ? '$edit' : undefined"
        :size="card ? 'small' : undefined"
      >
        <template v-if="!card">Add Free Card</template>
      </v-btn>
    </template>
    <v-card :title="card ? 'Edit Card' : 'Add Free Card'" :loading="saving">
      <v-card-text>
        <v-alert v-if="error" type="error" role="alert" class="mb-4">{{
          error
        }}</v-alert>
        <v-text-field
          v-model="cardNum"
          label="Card Number"
          maxlength="7"
          :disabled="saving"
          :error-messages="fieldErrors.cardNum"
          :rules="[
            (value: string) =>
              /^\d{1,7}$/.test(value) || 'Enter 1 to 7 digits.',
          ]"
        />
        <v-switch
          v-model="active"
          label="Active"
          :disabled="saving"
          :error-messages="fieldErrors.active"
        />
        <v-date-input
          v-if="!card"
          v-model="issueDate"
          label="Issue Date"
          :disabled="saving"
          :error-messages="fieldErrors.issueDate"
        />
      </v-card-text>
      <v-card-actions>
        <v-btn :disabled="saving" @click="dialog = false">Cancel</v-btn>
        <v-btn
          :loading="saving"
          :disabled="!/^\d{1,7}$/.test(cardNum) || (!card && !issueDate)"
          @click="submit"
          >Save</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
